import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../../../components/Sidebar';
import Navbar from '../../../components/Navbar';
import { useNavigate, useLocation } from 'react-router-dom';
import { getConversationList, searchConvoById } from '../../../services/dataApi/dataProvider';
import Pagination from '@mui/material/Pagination';
import { ApiUrlProvide, decryptURL, encryptURL, headerProvider, showToastMessage } from '../../../common/CommonFunctions';
import { Spinner, useToast } from '@chakra-ui/react';
import { FaBookmark } from 'react-icons/fa6';




const ConversationList = () => {
    const Navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const queryParams = new URLSearchParams(location.search);
    const pageNumberFromURL = queryParams.get('page') || 1;
    const sortingFieldFromURL = decryptURL(queryParams.get('sortField')) || 'localDateTime';
    const sortingOrderFromURL = decryptURL(queryParams.get('sortOrder')) || 'DESC';
    const userIdentifierFromURL = queryParams.get('userIdentifier') || '';
    const [loader, setLoader] = useState(false);
    const [conversationList, setConversationList] = useState([]);
    const [pageCount, setPageCount] = useState(0);
    const [itemsPerPage] = useState(10);
    const [filter, setFilter] = useState({});
    const [pageNumber, setPageNumber] = useState(pageNumberFromURL);
    const [sortingConvo, setSortingConvo] = useState({
        field: sortingFieldFromURL,
        order: sortingOrderFromURL,
    });
    const getAllConversation = useCallback(async () => {
        try {
            setLoader(true);
            const headers = headerProvider();
            if (userIdentifierFromURL) {
                const response = await searchConvoById({ method: 'GET', headers }, userIdentifierFromURL);
                if (response) {
                    setConversationList(response.response);
                    setPageCount(Math.ceil(response.totalCount / itemsPerPage));
                }
            } else {
                const queryParams = ApiUrlProvide(filter, pageNumber, itemsPerPage, sortingConvo.field, sortingConvo.order);
                const response = await getConversationList(queryParams, headers);
                if (response) {
                    setConversationList(response.response);
                    setPageCount(Math.ceil(response.totalCount / itemsPerPage));
                } else {
                    showToastMessage('Server Error', 'The server is not responding. Please try later!', 'error', toast);
                }
            }
            setLoader(false);
        } catch (error) {
            console.log(error);
            setLoader(false);
        }
    }, [filter, pageNumber, itemsPerPage, sortingConvo.field, sortingConvo.order, toast, userIdentifierFromURL]);

    const handleClick = (item) => {
        if (item) {
            Navigate(`/conversation/${item.id}`, { state: item.id });
        }
    };

    const handleClickRefresh = () => {
        Navigate(`?page=1&sortField=${encryptURL(sortingConvo.field)}&sortOrder=${encryptURL(sortingConvo.order)}`);
    };

    const handleClickSearch = async (userIdentifier) => {
        if (userIdentifier) {
            const headers = headerProvider();
            const requestOptions = {
                method: 'GET',
                headers,
            };
            const response = await searchConvoById(requestOptions, userIdentifier);
            if (response.response.length !== 0) {
                setConversationList(response.response);
                setPageCount(Math.ceil(response.totalCount / itemsPerPage));
                Navigate(`?userIdentifier=${userIdentifier}&page=1&sortField=${encryptURL(sortingConvo.field)}&sortOrder=${encryptURL(sortingConvo.order)}`);
            }
            else {
                showToastMessage('Error', 'This user ID does not exist.', 'error', toast)
            }
        }
        else {
            showToastMessage('Error', 'Please enter a valid id to search.', 'error', toast)
        }
    };

    const sortByMethod = (e) => {
        if (e.field) {
            setSortingConvo({ field: e.field, order: e.order });
            Navigate(`?page=${pageNumber}&sortField=${encryptURL(e.field)}&sortOrder=${encryptURL(e.order)}${userIdentifierFromURL ? `&userIdentifier=${userIdentifierFromURL}` : ''}`);
        }
    };

    const handleChange = (event, value) => {
        if (value) {
            Navigate(`?page=${value}&sortField=${encryptURL(sortingConvo.field)}&sortOrder=${encryptURL(sortingConvo.order)}${userIdentifierFromURL ? `&userIdentifier=${userIdentifierFromURL}` : ''}`);
        }
    };

    useEffect(() => {
        getAllConversation();
    }, [getAllConversation]);

    return (
        <div className='h-screen flex'>
            <div className='basis-1/5'>
                <Sidebar />
            </div>
            <div className='flex basis-4/5 flex-col bg-[#f4f7fe]'>
                <div>
                    <Navbar handleClickRefresh={handleClickRefresh} handleClickSearch={handleClickSearch} sortByMethod={sortByMethod} />
                </div>
                {
                    loader ? (
                        <div className='flex justify-center self-center mt-40'>
                            <Spinner
                                thickness='4px'
                                speed='0.65s'
                                emptyColor='gray.200'
                                color='blue.500'
                                size='xl'
                            />
                        </div>
                    ) : (
                        <div className="relative overflow-x-auto shadow-md sm:rounded-lg mb-6">
                            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                                <thead className="text-xs text-white uppercase bg-gray-700 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">Username</th>
                                        <th scope="col" className="px-6 py-3">User ID</th>
                                        <th scope="col" className="px-6 py-3">Therapist</th>
                                        <th scope="col" className="px-6 py-3">Type</th>
                                        <th scope="col" className="px-6 py-3">Messages count</th>
                                        <th scope="col" className="px-6 py-3">Conversation Status</th>
                                        <th scope="col" className="px-6 py-3">Expiring on</th>
                                        <th scope="col" className="px-6 py-3">Updated Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        conversationList.map((item) => (
                                            <tr
                                                key={item.id}
                                                onClick={() => handleClick(item)}
                                                className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700 cursor-pointer hover:bg-gray-100 w-full"
                                            >
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold'>{item?.userName?.slice(0, 12)}</p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className={`text-base font-semibold border-2 rounded-md text-center ${item?.isCritical ? 'border-red-500' : 'border-transparent'}`}>
                                                        {item?.userIdentifier || 'NA'}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold'>{item?.volunteerName ? item.volunteerName : item.therapistName}</p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold'>{item.type}</p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold text-center'>{item.messagesCount} {item.isRead && (<span className='text-sm'>
                                                        • Seen
                                                    </span>)}</p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    {(() => {
                                                        let statusText = '';
                                                        let bgColor = '';
                                                        if (!item.isActive) {
                                                            statusText = 'Completed';
                                                            bgColor = 'bg-green-500';
                                                        } else if (new Date() >= new Date(item.expiringOn)) {
                                                            statusText = 'Expired';
                                                            bgColor = 'bg-red-500';
                                                        } else {
                                                            statusText = 'In Progress';
                                                            bgColor = 'bg-blue-500';
                                                        }
                                                        return (
                                                            <div className={`flex items-center justify-between text-sm font-semibold text-white rounded-md p-1 px-3 w-full ${bgColor}`}>
                                                                <span className="mx-auto">{statusText}</span>
                                                                {item?.bookmarked && (
                                                                    <FaBookmark size={14} />
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold'>{new Date(item.expiringOn).toLocaleDateString()}</p>
                                                </td>
                                                <td className="px-6 py-2">
                                                    <p className='text-base font-semibold'>{new Date(item.updatedDate).toLocaleDateString()}</p>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    )
                }
                {
                    !loader && (
                        <div className='mb-10 flex justify-center h-20 items-center'>
                            <div>
                                <Pagination
                                    page={parseInt(pageNumber)}
                                    count={pageCount}
                                    color="primary"
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    );
};

export default ConversationList;